import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  MessageCircle, Settings, Send, FileText, Zap, Plus, Trash2, Edit,
  Loader2, CheckCircle2, XCircle, RefreshCw, AlertCircle, Key,
  Building2, Users, Activity, Bot, PhoneCall, Layers,
  ChevronRight, ChevronDown, GripVertical, ToggleLeft, ToggleRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const TEMPLATE_LANGUAGES = [
  { id: 'es', label: 'Español' },
  { id: 'es_MX', label: 'Español (México)' },
  { id: 'en_US', label: 'English (US)' },
  { id: 'pt_BR', label: 'Português' },
  { id: 'fr', label: 'Français' },
];

const SAMPLE_TEMPLATES = [
  {
    name: 'bienvenida_reservacion',
    language: 'es',
    category: 'UTILITY',
    header_text: '¡Tu reserva está confirmada! ✅',
    body_text: 'Hola {{1}}, tu mesa para {{2}} personas el {{3}} a las {{4}} en {{5}} está confirmada. Código: *{{6}}*. ¡Te esperamos!',
    footer_text: 'OkReserva · Responde CANCELAR para anular',
    variables: ['nombre', 'personas', 'fecha', 'hora', 'restaurante', 'codigo'],
    description: 'Confirmación automática de reserva'
  },
  {
    name: 'recordatorio_reserva',
    language: 'es',
    category: 'UTILITY',
    header_text: '⏰ Recordatorio de reserva',
    body_text: 'Hola {{1}}! Mañana tienes reserva en *{{2}}* a las {{3}} para {{4}} personas. Dirección: {{5}}',
    footer_text: 'Responde para contactarnos',
    variables: ['nombre', 'restaurante', 'hora', 'personas', 'direccion'],
    description: 'Recordatorio 24h antes de la reserva'
  },
  {
    name: 'promocion_especial',
    language: 'es',
    category: 'MARKETING',
    header_text: '🎉 Oferta exclusiva para ti',
    body_text: 'Hola {{1}}, tenemos una oferta especial en *{{2}}*: {{3}}. Válida hasta el {{4}}. Usa el código *{{5}}*.',
    footer_text: 'Responde NO para no recibir más ofertas',
    variables: ['nombre', 'restaurante', 'oferta', 'fecha_vencimiento', 'codigo'],
    description: 'Campaña de marketing con código promocional'
  },
];

export default function AdminKapso() {
  const [mainTab, setMainTab] = useState('config');

  // Config
  const [configInfo, setConfigInfo] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [configForm, setConfigForm] = useState({ team_phone_number: '', team_name: 'OkReserva', webhook_url: '', notes: '' });
  const [configLoading, setConfigLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Panel 1 — For Your Team
  const [teamTab, setTeamTab] = useState('send');
  const [teamSendForm, setTeamSendForm] = useState({ to_phone: '', message: '' });
  const [teamSending, setTeamSending] = useState(false);
  const [teamMessages, setTeamMessages] = useState([]);
  const [teamMessagesLoading, setTeamMessagesLoading] = useState(false);

  // Panel 2 — Customer (templates + flows + restaurants)
  const [customerTab, setCustomerTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', language: 'es', category: 'MARKETING', header_text: '', body_text: '', footer_text: '', buttons: [], variables: [], description: '' });
  const [showSamplePicker, setShowSamplePicker] = useState(false);
  const [flows, setFlows] = useState([]);
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [showFlowDialog, setShowFlowDialog] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [flowForm, setFlowForm] = useState({ name: '', description: '', trigger_keywords: [], is_active: true, steps: [] });
  const [triggerInput, setTriggerInput] = useState('');
  const [restaurantKapsoConfigs, setRestaurantKapsoConfigs] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignForm, setAssignForm] = useState({ restaurant_id: '', phone_number: '', display_name: '', webhook_url: '' });

  const adminToken = localStorage.getItem('admin_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` };

  useEffect(() => { loadConfig(); }, []);

  async function loadConfig() {
    try {
      const r = await fetch(`${API_URL}/api/kapso/admin/config`, { headers });
      if (r.ok) {
        const d = await r.json();
        setConfigInfo(d);
        setConfigForm({ team_phone_number: d.team_phone_number || '', team_name: d.team_name || 'OkReserva', webhook_url: d.webhook_url || '', notes: d.notes || '' });
      }
    } catch {}
  }

  async function saveConfig() {
    if (!apiKey.trim()) { toast.error('API Key requerida'); return; }
    setConfigLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/kapso/admin/config`, {
        method: 'POST', headers, body: JSON.stringify({ api_key: apiKey, ...configForm }),
      });
      const d = await r.json();
      if (d.ok) { toast.success(d.message); setApiKey(''); loadConfig(); }
      else toast.error(d.detail || 'Error al guardar');
    } catch { toast.error('Error de conexión'); }
    finally { setConfigLoading(false); }
  }

  async function testConnection() {
    setTesting(true); setTestResult(null);
    try {
      const r = await fetch(`${API_URL}/api/kapso/admin/test-connection`, { method: 'POST', headers });
      const d = await r.json();
      setTestResult(d);
      if (d.ok) toast.success(d.message); else toast.error(d.message);
    } catch { toast.error('Error'); }
    finally { setTesting(false); }
  }

  // Team functions
  async function sendTeamMessage() {
    if (!teamSendForm.to_phone || !teamSendForm.message) { toast.error('Teléfono y mensaje requeridos'); return; }
    setTeamSending(true);
    try {
      const r = await fetch(`${API_URL}/api/kapso/admin/team/send`, {
        method: 'POST', headers, body: JSON.stringify(teamSendForm),
      });
      const d = await r.json();
      if (d.ok) { toast.success(d.message); setTeamSendForm({ to_phone: '', message: '' }); loadTeamMessages(); }
      else toast.error(d.detail || 'Error al enviar');
    } catch { toast.error('Error'); }
    finally { setTeamSending(false); }
  }

  async function loadTeamMessages() {
    setTeamMessagesLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/kapso/admin/team/messages`, { headers });
      if (r.ok) { const d = await r.json(); setTeamMessages(d.messages || []); }
    } catch {}
    finally { setTeamMessagesLoading(false); }
  }

  // Templates
  async function loadTemplates() {
    setTemplatesLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/kapso/admin/templates`, { headers });
      if (r.ok) { const d = await r.json(); setTemplates(d.templates || []); }
    } catch {}
    finally { setTemplatesLoading(false); }
  }

  async function saveTemplate() {
    if (!templateForm.name || !templateForm.body_text) { toast.error('Nombre y cuerpo requeridos'); return; }
    const url = editingTemplate
      ? `${API_URL}/api/kapso/admin/templates/${editingTemplate.id}`
      : `${API_URL}/api/kapso/admin/templates`;
    const method = editingTemplate ? 'PUT' : 'POST';
    try {
      const r = await fetch(url, { method, headers, body: JSON.stringify(templateForm) });
      const d = await r.json();
      if (d.ok || d.id) { toast.success(editingTemplate ? 'Plantilla actualizada' : 'Plantilla creada'); setShowTemplateDialog(false); loadTemplates(); }
      else toast.error(d.detail || 'Error');
    } catch { toast.error('Error'); }
  }

  async function deleteTemplate(id) {
    if (!window.confirm('¿Eliminar plantilla?')) return;
    await fetch(`${API_URL}/api/kapso/admin/templates/${id}`, { method: 'DELETE', headers });
    toast.success('Eliminada'); loadTemplates();
  }

  // Flows
  async function loadFlows() {
    setFlowsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/kapso/admin/flows`, { headers });
      if (r.ok) { const d = await r.json(); setFlows(d.flows || []); }
    } catch {}
    finally { setFlowsLoading(false); }
  }

  async function saveFlow() {
    if (!flowForm.name) { toast.error('Nombre requerido'); return; }
    const url = editingFlow ? `${API_URL}/api/kapso/admin/flows/${editingFlow.id}` : `${API_URL}/api/kapso/admin/flows`;
    const method = editingFlow ? 'PUT' : 'POST';
    try {
      const r = await fetch(url, { method, headers, body: JSON.stringify(flowForm) });
      const d = await r.json();
      if (d.ok || d.id) { toast.success(editingFlow ? 'Flow actualizado' : 'Flow creado'); setShowFlowDialog(false); loadFlows(); }
      else toast.error(d.detail || 'Error');
    } catch { toast.error('Error'); }
  }

  async function toggleFlow(id) {
    try {
      const r = await fetch(`${API_URL}/api/kapso/admin/flows/${id}/toggle`, { method: 'PATCH', headers });
      if (r.ok) { loadFlows(); toast.success('Estado actualizado'); }
    } catch {}
  }

  async function deleteFlow(id) {
    if (!window.confirm('¿Eliminar flow?')) return;
    await fetch(`${API_URL}/api/kapso/admin/flows/${id}`, { method: 'DELETE', headers });
    toast.success('Flow eliminado'); loadFlows();
  }

  // Restaurant configs
  async function loadRestaurantConfigs() {
    try {
      const [cfgR, restR] = await Promise.all([
        fetch(`${API_URL}/api/kapso/admin/customers/restaurants`, { headers }),
        fetch(`${API_URL}/api/admin/restaurants`, { headers }),
      ]);
      if (cfgR.ok) { const d = await cfgR.json(); setRestaurantKapsoConfigs(d.configs || []); }
      if (restR.ok) { const d = await restR.json(); setRestaurants(d.restaurants || d || []); }
    } catch {}
  }

  async function assignRestaurant() {
    if (!assignForm.restaurant_id || !assignForm.phone_number) { toast.error('Restaurante y teléfono requeridos'); return; }
    try {
      const r = await fetch(`${API_URL}/api/kapso/admin/customers/configure?restaurant_id=${assignForm.restaurant_id}`, {
        method: 'POST', headers, body: JSON.stringify({ phone_number: assignForm.phone_number, display_name: assignForm.display_name, webhook_url: assignForm.webhook_url }),
      });
      const d = await r.json();
      if (d.ok) { toast.success(d.message); setShowAssignDialog(false); loadRestaurantConfigs(); }
      else toast.error(d.detail || 'Error');
    } catch { toast.error('Error'); }
  }

  function openEditTemplate(t) {
    setEditingTemplate(t);
    setTemplateForm({ name: t.name, language: t.language, category: t.category, header_text: t.header_text, body_text: t.body_text, footer_text: t.footer_text, buttons: t.buttons || [], variables: t.variables || [], description: t.description || '' });
    setShowTemplateDialog(true);
  }

  function openEditFlow(f) {
    setEditingFlow(f);
    setFlowForm({ name: f.name, description: f.description || '', trigger_keywords: f.trigger_keywords || [], is_active: f.is_active, steps: f.steps || [] });
    setTriggerInput('');
    setShowFlowDialog(true);
  }

  function addTrigger() {
    if (!triggerInput.trim()) return;
    setFlowForm(f => ({ ...f, trigger_keywords: [...f.trigger_keywords, triggerInput.trim().toLowerCase()] }));
    setTriggerInput('');
  }

  function removeTrigger(kw) {
    setFlowForm(f => ({ ...f, trigger_keywords: f.trigger_keywords.filter(k => k !== kw) }));
  }

  const StatusBadge = ({ ok }) => ok
    ? <Badge className="bg-green-100 text-green-700 border-green-200 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />Activo</Badge>
    : <Badge variant="outline" className="text-gray-500 text-xs">Inactivo</Badge>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Kapso Server — WhatsApp IA</h1>
        <p className="text-sm text-gray-500 mt-1">Plataforma de mensajería WhatsApp para OkReserva y sus restaurantes.</p>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="config"><Settings className="h-4 w-4 mr-1.5" />Configuración</TabsTrigger>
          <TabsTrigger value="team" onClick={() => { if (teamMessages.length === 0) loadTeamMessages(); }}>
            <Users className="h-4 w-4 mr-1.5" />For Your Team
          </TabsTrigger>
          <TabsTrigger value="customer" onClick={() => { if (templates.length === 0) loadTemplates(); if (flows.length === 0) loadFlows(); if (restaurantKapsoConfigs.length === 0) loadRestaurantConfigs(); }}>
            <Building2 className="h-4 w-4 mr-1.5" />Customer Guide
          </TabsTrigger>
        </TabsList>

        {/* ── CONFIG ── */}
        <TabsContent value="config" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Key className="h-5 w-5 text-orange-500" />Kapso API Key</CardTitle>
              <CardDescription>
                Obtén tu API key en <a href="https://kapso.ai" target="_blank" rel="noreferrer" className="text-orange-500 underline">kapso.ai</a> · Docs: <a href="https://docs.kapso.ai" target="_blank" rel="noreferrer" className="text-orange-500 underline">docs.kapso.ai</a>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {configInfo && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${configInfo.configured ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                  {configInfo.configured ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                  {configInfo.configured ? `API key configurada: ${configInfo.api_key_masked}` : 'Sin configurar. Ingresa tu API key de Kapso.'}
                  {configInfo.last_test && (
                    <span className="ml-auto text-xs opacity-70">
                      Prueba: {new Date(configInfo.last_test).toLocaleString('es-MX')} — {configInfo.last_test_status === 'success' ? '✓' : '✗'}
                    </span>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Label>Nueva API Key</Label>
                <Input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="kap_xxxxxxxxxxxxxxxx" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre del equipo</Label>
                  <Input value={configForm.team_name} onChange={e => setConfigForm(f => ({ ...f, team_name: e.target.value }))} placeholder="OkReserva" />
                </div>
                <div className="space-y-2">
                  <Label>Número WhatsApp del equipo (For Your Team)</Label>
                  <Input value={configForm.team_phone_number} onChange={e => setConfigForm(f => ({ ...f, team_phone_number: e.target.value }))} placeholder="+521234567890" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Webhook URL</Label>
                  <Input value={configForm.webhook_url} onChange={e => setConfigForm(f => ({ ...f, webhook_url: e.target.value }))} placeholder="https://tu-dominio.com/api/webhook/kapso" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Notas</Label>
                  <Textarea value={configForm.notes} onChange={e => setConfigForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Notas internas..." />
                </div>
              </div>
              <div className="flex gap-3 flex-wrap">
                <Button onClick={saveConfig} disabled={configLoading} className="bg-orange-500 hover:bg-orange-600">
                  {configLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Guardar API Key
                </Button>
                <Button variant="outline" onClick={testConnection} disabled={testing}>
                  {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Activity className="h-4 w-4 mr-2" />}Probar Conexión
                </Button>
              </div>
              {testResult && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${testResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {testResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}{testResult.message}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-orange-100 bg-orange-50/30 cursor-pointer hover:bg-orange-50 transition-colors" onClick={() => setMainTab('team')}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><Users className="h-5 w-5 text-orange-500" />For Your Team</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">OkReserva usa Kapso con su número digital propio para comunicaciones internas y con restaurantes.</p>
                <p className="text-xs text-orange-500 mt-3 font-medium">Número: {configInfo?.team_phone_number || 'No configurado'}</p>
              </CardContent>
            </Card>
            <Card className="border-blue-100 bg-blue-50/30 cursor-pointer hover:bg-blue-50 transition-colors" onClick={() => setMainTab('customer')}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><Building2 className="h-5 w-5 text-blue-500" />Customer Guide</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">Los restaurantes usan su propio número WhatsApp con Kapso para comunicarse con sus clientes.</p>
                <p className="text-xs text-blue-500 mt-3 font-medium">{restaurantKapsoConfigs.length} restaurante(s) configurado(s)</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── PANEL 1: FOR YOUR TEAM ── */}
        <TabsContent value="team" className="mt-4">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <Users className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">For Your Team</h2>
                <p className="text-sm text-gray-500">Kapso como motor de WhatsApp del equipo OkReserva · Número: {configInfo?.team_phone_number || '—'}</p>
              </div>
            </div>

            <Tabs value={teamTab} onValueChange={setTeamTab}>
              <TabsList>
                <TabsTrigger value="send"><Send className="h-4 w-4 mr-1.5" />Enviar Mensaje</TabsTrigger>
                <TabsTrigger value="messages" onClick={loadTeamMessages}><MessageCircle className="h-4 w-4 mr-1.5" />Mensajes</TabsTrigger>
              </TabsList>

              <TabsContent value="send" className="mt-4">
                <Card>
                  <CardHeader><CardTitle className="text-base">Enviar WhatsApp desde OkReserva</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Número destinatario (formato E.164)</Label>
                      <Input value={teamSendForm.to_phone} onChange={e => setTeamSendForm(f => ({ ...f, to_phone: e.target.value }))} placeholder="+521234567890" />
                    </div>
                    <div className="space-y-2">
                      <Label>Mensaje</Label>
                      <Textarea value={teamSendForm.message} onChange={e => setTeamSendForm(f => ({ ...f, message: e.target.value }))} rows={4} placeholder="Hola, te contactamos de OkReserva..." />
                    </div>
                    <Button onClick={sendTeamMessage} disabled={teamSending} className="bg-orange-500 hover:bg-orange-600">
                      {teamSending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}Enviar WhatsApp
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="messages" className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-gray-500">{teamMessages.length} mensaje(s)</p>
                  <Button variant="outline" size="sm" onClick={loadTeamMessages} disabled={teamMessagesLoading}>
                    <RefreshCw className={`h-4 w-4 mr-1.5 ${teamMessagesLoading ? 'animate-spin' : ''}`} />Actualizar
                  </Button>
                </div>
                <Card>
                  {teamMessages.length === 0
                    ? <CardContent className="py-12 text-center"><MessageCircle className="h-12 w-12 text-gray-200 mx-auto mb-3" /><p className="text-gray-400">Sin mensajes enviados</p></CardContent>
                    : <div className="divide-y">{teamMessages.map(m => (
                      <div key={m.id} className="flex items-start justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                            <MessageCircle className="h-4 w-4 text-green-500" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{m.to}</p>
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{m.message}</p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          <Badge className={m.status === 'sent' ? 'bg-green-100 text-green-700 text-xs' : 'bg-red-100 text-red-700 text-xs'}>
                            {m.status === 'sent' ? '✓ Enviado' : 'Error'}
                          </Badge>
                          <p className="text-xs text-gray-400 mt-1">{new Date(m.created_at).toLocaleString('es-MX')}</p>
                        </div>
                      </div>
                    ))}</div>
                  }
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </TabsContent>

        {/* ── PANEL 2: CUSTOMER GUIDE ── */}
        <TabsContent value="customer" className="mt-4">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Building2 className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Customer Guide</h2>
                <p className="text-sm text-gray-500">Restaurantes con su propio número WhatsApp — templates, flows y configuración por restaurante</p>
              </div>
            </div>

            <Tabs value={customerTab} onValueChange={v => { setCustomerTab(v); if (v === 'templates' && templates.length === 0) loadTemplates(); if (v === 'flows' && flows.length === 0) loadFlows(); if (v === 'restaurants' && restaurantKapsoConfigs.length === 0) loadRestaurantConfigs(); }}>
              <TabsList>
                <TabsTrigger value="templates"><FileText className="h-4 w-4 mr-1.5" />Templates</TabsTrigger>
                <TabsTrigger value="flows"><Zap className="h-4 w-4 mr-1.5" />Flows</TabsTrigger>
                <TabsTrigger value="restaurants"><Building2 className="h-4 w-4 mr-1.5" />Restaurantes</TabsTrigger>
              </TabsList>

              {/* TEMPLATES */}
              <TabsContent value="templates" className="mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">{templates.length} template(s) WhatsApp</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={loadTemplates} disabled={templatesLoading}>
                      <RefreshCw className={`h-4 w-4 mr-1.5 ${templatesLoading ? 'animate-spin' : ''}`} />Actualizar
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowSamplePicker(true)}>
                      <Zap className="h-4 w-4 mr-1.5" />Usar muestra
                    </Button>
                    <Button size="sm" className="bg-orange-500 hover:bg-orange-600"
                      onClick={() => { setEditingTemplate(null); setTemplateForm({ name: '', language: 'es', category: 'MARKETING', header_text: '', body_text: '', footer_text: '', buttons: [], variables: [], description: '' }); setShowTemplateDialog(true); }}>
                      <Plus className="h-4 w-4 mr-1.5" />Nuevo Template
                    </Button>
                  </div>
                </div>

                {templatesLoading ? <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
                  : templates.length === 0 ? <Card><CardContent className="py-10 text-center"><FileText className="h-10 w-10 text-gray-200 mx-auto mb-3" /><p className="text-gray-400">Sin templates. Crea el primero.</p></CardContent></Card>
                  : <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {templates.map(t => (
                      <Card key={t.id}>
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-sm font-mono">{t.name}</CardTitle>
                              <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>
                            </div>
                            <div className="flex gap-1">
                              <Badge variant="outline" className="text-xs">{t.category}</Badge>
                              <Badge variant="outline" className="text-xs">{t.language}</Badge>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {t.header_text && <p className="text-xs font-semibold text-gray-700 mb-1">📌 {t.header_text}</p>}
                          <p className="text-xs text-gray-600 line-clamp-2 bg-gray-50 rounded p-2">{t.body_text}</p>
                          {t.footer_text && <p className="text-xs text-gray-400 mt-1">📎 {t.footer_text}</p>}
                          <div className="flex gap-1 mt-3 items-center">
                            <Badge className={`text-xs ${t.status === 'approved' ? 'bg-green-100 text-green-700' : t.status === 'submitted' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                              {t.status || 'local'}
                            </Badge>
                            <div className="ml-auto flex gap-1">
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditTemplate(t)}><Edit className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => deleteTemplate(t.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                }
              </TabsContent>

              {/* FLOWS */}
              <TabsContent value="flows" className="mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">{flows.length} flow(s) de conversación</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={loadFlows} disabled={flowsLoading}>
                      <RefreshCw className={`h-4 w-4 mr-1.5 ${flowsLoading ? 'animate-spin' : ''}`} />Actualizar
                    </Button>
                    <Button size="sm" className="bg-orange-500 hover:bg-orange-600"
                      onClick={() => { setEditingFlow(null); setFlowForm({ name: '', description: '', trigger_keywords: [], is_active: true, steps: [] }); setTriggerInput(''); setShowFlowDialog(true); }}>
                      <Plus className="h-4 w-4 mr-1.5" />Nuevo Flow
                    </Button>
                  </div>
                </div>

                {flowsLoading ? <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
                  : flows.length === 0 ? <Card><CardContent className="py-10 text-center"><Zap className="h-10 w-10 text-gray-200 mx-auto mb-3" /><p className="text-gray-400">Sin flows configurados.</p><p className="text-xs text-gray-400 mt-1">Los flows automatizan respuestas WhatsApp basadas en palabras clave.</p></CardContent></Card>
                  : <div className="space-y-3">
                    {flows.map(f => (
                      <Card key={f.id} className={f.is_active ? '' : 'opacity-60'}>
                        <CardContent className="py-3 px-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${f.is_active ? 'bg-orange-50' : 'bg-gray-100'}`}>
                                <Zap className={`h-4 w-4 ${f.is_active ? 'text-orange-500' : 'text-gray-400'}`} />
                              </div>
                              <div>
                                <p className="font-medium text-sm text-gray-900">{f.name}</p>
                                <p className="text-xs text-gray-500">{f.description || 'Sin descripción'}</p>
                                <div className="flex gap-1 mt-1 flex-wrap">
                                  {(f.trigger_keywords || []).map(kw => (
                                    <span key={kw} className="px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-600 font-mono">{kw}</span>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={f.is_active ? 'bg-green-100 text-green-700 text-xs' : 'bg-gray-100 text-gray-500 text-xs'}>
                                {f.is_active ? 'Activo' : 'Inactivo'}
                              </Badge>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => toggleFlow(f.id)}>
                                {f.is_active ? <ToggleRight className="h-4 w-4 text-green-500" /> : <ToggleLeft className="h-4 w-4 text-gray-400" />}
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditFlow(f)}><Edit className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => deleteFlow(f.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                }
              </TabsContent>

              {/* RESTAURANTS */}
              <TabsContent value="restaurants" className="mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">{restaurantKapsoConfigs.length} restaurante(s) con Kapso</p>
                  <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={() => { setAssignForm({ restaurant_id: '', phone_number: '', display_name: '', webhook_url: '' }); setShowAssignDialog(true); }}>
                    <Plus className="h-4 w-4 mr-1.5" />Vincular Restaurante
                  </Button>
                </div>
                {restaurantKapsoConfigs.length === 0
                  ? <Card><CardContent className="py-10 text-center"><Building2 className="h-10 w-10 text-gray-200 mx-auto mb-3" /><p className="text-gray-400">Sin restaurantes vinculados a Kapso.</p></CardContent></Card>
                  : <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {restaurantKapsoConfigs.map(c => (
                      <Card key={c.id}>
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
                              <MessageCircle className="h-5 w-5 text-green-500" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{c.restaurant_name}</p>
                              <p className="font-mono text-xs text-gray-500">{c.phone_number}</p>
                              {c.display_name && <p className="text-xs text-gray-400">{c.display_name}</p>}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                }
              </TabsContent>
            </Tabs>
          </div>
        </TabsContent>
      </Tabs>

      {/* ── TEMPLATE DIALOG ── */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingTemplate ? 'Editar Template WhatsApp' : 'Nuevo Template WhatsApp'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Nombre (snake_case) *</Label>
                <Input value={templateForm.name} onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value.toLowerCase().replace(/\s+/g,'_') }))} placeholder="nombre_plantilla" className="font-mono" />
              </div>
              <div className="space-y-1">
                <Label>Idioma</Label>
                <Select value={templateForm.language} onValueChange={v => setTemplateForm(f => ({ ...f, language: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TEMPLATE_LANGUAGES.map(l => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Categoría</Label>
                <Select value={templateForm.category} onValueChange={v => setTemplateForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TEMPLATE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Descripción</Label>
                <Input value={templateForm.description} onChange={e => setTemplateForm(f => ({ ...f, description: e.target.value }))} placeholder="Para qué se usa..." />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Encabezado (Header) — opcional</Label>
              <Input value={templateForm.header_text} onChange={e => setTemplateForm(f => ({ ...f, header_text: e.target.value }))} placeholder="¡Tu reserva está confirmada! ✅" />
            </div>
            <div className="space-y-1">
              <Label>Cuerpo (Body) * — usa {'{{1}}'}, {'{{2}}'} para variables</Label>
              <Textarea value={templateForm.body_text} onChange={e => setTemplateForm(f => ({ ...f, body_text: e.target.value }))} rows={5}
                placeholder="Hola {{1}}, tu reserva para {{2}} personas el {{3}} está confirmada." />
              <p className="text-xs text-gray-400">Usa {'{{1}}'}, {'{{2}}'}, ... para variables que se reemplazarán al enviar</p>
            </div>
            <div className="space-y-1">
              <Label>Pie de página (Footer) — opcional</Label>
              <Input value={templateForm.footer_text} onChange={e => setTemplateForm(f => ({ ...f, footer_text: e.target.value }))} placeholder="OkReserva · Responde CANCELAR para anular" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancelar</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={saveTemplate}>{editingTemplate ? 'Guardar' : 'Crear Template'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── FLOW DIALOG ── */}
      <Dialog open={showFlowDialog} onOpenChange={setShowFlowDialog}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingFlow ? 'Editar Flow' : 'Nuevo Flow de Conversación'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Nombre del flow *</Label>
              <Input value={flowForm.name} onChange={e => setFlowForm(f => ({ ...f, name: e.target.value }))} placeholder="Reservaciones automáticas" />
            </div>
            <div className="space-y-1">
              <Label>Descripción</Label>
              <Textarea value={flowForm.description} onChange={e => setFlowForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Qué hace este flow..." />
            </div>
            <div className="space-y-2">
              <Label>Palabras clave de activación</Label>
              <div className="flex gap-2">
                <Input value={triggerInput} onChange={e => setTriggerInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTrigger(); } }}
                  placeholder="reservar, hola, info..." className="flex-1" />
                <Button variant="outline" size="sm" onClick={addTrigger}>+Agregar</Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {flowForm.trigger_keywords.map(kw => (
                  <span key={kw} className="flex items-center gap-1 px-2 py-0.5 bg-orange-50 text-orange-700 rounded text-xs font-mono">
                    {kw}
                    <button onClick={() => removeTrigger(kw)} className="ml-1 text-orange-400 hover:text-red-500">×</button>
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={flowForm.is_active} onCheckedChange={v => setFlowForm(f => ({ ...f, is_active: v }))} />
              <Label>{flowForm.is_active ? 'Activo' : 'Inactivo'}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFlowDialog(false)}>Cancelar</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={saveFlow}>{editingFlow ? 'Guardar' : 'Crear Flow'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── SAMPLE TEMPLATE PICKER ── */}
      <Dialog open={showSamplePicker} onOpenChange={setShowSamplePicker}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Templates de Muestra</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {SAMPLE_TEMPLATES.map(s => (
              <div key={s.name} className="border rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => { setEditingTemplate(null); setTemplateForm({ name: s.name, language: s.language, category: s.category, header_text: s.header_text, body_text: s.body_text, footer_text: s.footer_text, buttons: [], variables: s.variables, description: s.description }); setShowSamplePicker(false); setShowTemplateDialog(true); }}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-mono font-medium text-sm text-gray-900">{s.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{s.description}</p>
                  </div>
                  <div className="flex gap-1">
                    <Badge variant="outline" className="text-xs">{s.category}</Badge>
                    <Badge variant="outline" className="text-xs">{s.language}</Badge>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2 bg-green-50 rounded p-2 line-clamp-2">{s.body_text}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── ASSIGN RESTAURANT DIALOG ── */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Vincular Restaurante con Kapso</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Restaurante</Label>
              <Select value={assignForm.restaurant_id} onValueChange={v => { const r = restaurants.find(x => x.id === v); setAssignForm(f => ({ ...f, restaurant_id: v, display_name: r?.name || '' })); }}>
                <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>{restaurants.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Número WhatsApp del restaurante (E.164)</Label>
              <Input value={assignForm.phone_number} onChange={e => setAssignForm(f => ({ ...f, phone_number: e.target.value }))} placeholder="+521234567890" />
            </div>
            <div className="space-y-2">
              <Label>Nombre para mostrar</Label>
              <Input value={assignForm.display_name} onChange={e => setAssignForm(f => ({ ...f, display_name: e.target.value }))} placeholder="Restaurante XYZ" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancelar</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={assignRestaurant}>Vincular</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
